import React from "react";
import { Head } from "@inertiajs/react";
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { User, Mail, Calendar, MessageSquare, Settings } from "lucide-react";
import { UserSlackConnection } from "@/components/slack/user-slack-connection";
import { usePage } from "@inertiajs/react";

export default function Profile() {
    const { auth } = usePage().props;
    const user = auth.user;

    return (
        <>
            <Head title="DME | Profile" />
            <AuthenticatedLayout>
            <div className="flex flex-col gap-6 w-full px-4 md:px-6 pb-8 pt-6">
                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-bold tracking-tight">
                        Profile & Settings
                    </h1>
                    <p className="text-muted-foreground">
                        Manage your account settings and integrations
                    </p>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    {/* User Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Account Information
                            </CardTitle>
                            <CardDescription>
                                Your basic account details
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <div className="text-sm font-medium text-muted-foreground">Name</div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium">{user.name}</span>
                                    {user.status && (
                                        <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                                            {user.status}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <div className="text-sm font-medium text-muted-foreground">Email</div>
                                <div className="flex items-center gap-2">
                                    <Mail className="h-4 w-4 text-muted-foreground" />
                                    <span>{user.email}</span>
                                </div>
                            </div>

                            {user.department && (
                                <div className="space-y-2">
                                    <div className="text-sm font-medium text-muted-foreground">Department</div>
                                    <div className="flex items-center gap-2">
                                        <Settings className="h-4 w-4 text-muted-foreground" />
                                        <span>{user.department.name}</span>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <div className="text-sm font-medium text-muted-foreground">Member Since</div>
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span>{new Date(user.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Personal Integrations */}
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <MessageSquare className="h-5 w-5" />
                                    Personal Integrations
                                </CardTitle>
                                <CardDescription>
                                    Connect your personal accounts to enhance your workflow experience
                                </CardDescription>
                            </CardHeader>
                        </Card>

                        {/* Individual Slack Connection */}
                        <UserSlackConnection user={user} />
                    </div>
                </div>
            </div>
        </AuthenticatedLayout>
        </>
    );
} 